import Button from 'components/Form/Button';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ReadOne } from 'services/financing-proposals';
import { Title } from 'ui/styled';
import { exposeStrapiError } from 'utils';
import {
  esadoCivilOptions,
  genderOptions,
  nacionalidadeOptions,
  situacOptions,
} from 'utils/options';
import {
  formatCEP,
  formatDocuments,
  formatPhone,
  parseCurrency,
} from 'utils/parsers';
import { normalizeName } from 'utils/validators';
import FormCore from '../../components/Form/Core';
import {
  FinancingProposalFormsWrapper,
  FinancingProposalHeader,
  FormSpacing,
} from './styled';

const LOGO_URL = '/images/logo_sem_fundo.png';
// Function to convert an image URL to Base64
const getBase64Image = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };
    img.onerror = (error) => {
      console.error('Erro ao carregar a imagem para Base64:', error);
      reject(new Error('Erro ao carregar imagem.'));
    };
    img.src = url;
  });
};

export default function FinancingProposal() {
  const history = useHistory();
  const { id } = useParams();
  const { user } = useContext(CoreContext);
  const [register, setRegister] = useState(null);
  const [registerInstall, setRegisterInstall] = useState(null);
  const [registerPersonal, setRegisterPersonal] = useState(null);
  const [registerPartner, setRegisterPartner] = useState(null);
  const [registerProposals, setRegisterProposals] = useState([]);
  const [error, setError] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const refForm = useRef();
  const refFormAddressInstall = useRef();
  const refFormPJPF = useRef();

  useEffect(
    () =>
      getBase64Image(LOGO_URL)
        .then(setLogoBase64)
        .catch(() => {
          console.warn(
            'Não foi possível carregar a logo em Base64. O PDF usará o fallback de texto.'
          );
        }),
    []
  );

  const isPf = useMemo(() => {
    return registerPartner?.tcc2 !== 'juridica' && !registerPartner?.cnpj;
  }, [registerPartner]);
  const pessoFisJudi = [
    { id: 'fisica', title: 'Física', name: 'Física' },
    { id: 'juridica', title: 'Jurídica', name: 'Jurídica' },
  ];
  const getOptionTitle = (value, options) => {
    if (value == null) return 'N/A';
    const option = options.find((opt) => opt.id === value);
    return option ? option.title : value;
  };
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };
  const truncateText = (text, maxLength) => {
    if (text == null) return 'N/A';
    return typeof text === 'string' && text.length > maxLength
      ? text.substring(0, maxLength - 3) + '...'
      : text;
  };

  const generateFullProposalMirror = useCallback(async () => {
    if (
      !register ||
      !registerPartner ||
      !registerInstall ||
      (isPf && !registerPersonal)
    ) {
      alert(
        'Dados incompletos. Por favor, verifique as informações do formulário.'
      );
      return;
    }
    const fullProposalData = {
      id,
      projeto: register || {},
      cliente: registerPartner || {},
      profissional: registerPersonal || {},
      instalacao: registerInstall || {},
      propostas_banco:
        registerProposals.map((p) => ({
          banco: p.banco?.nome_fantasia || p.banco || 'N/A',
          status: p.status || 'N/A',
          emailB: p.emailB || 'N/A',
        })) || [],
    };
    const data = fullProposalData;
    const doc = new jsPDF('p', 'mm', 'a4');
    let y = 10;
    const MARGIN_LEFT = 8;
    const MARGIN_RIGHT = 8;
    const PAGE_WIDTH = 210;
    const PAGE_HEIGHT = 297;
    const LINE_HEIGHT = 7;
    const TABLE_PADDING = 2.5;
    const LOGO_MAX_HEIGHT = 40;
    const LOGO_MAX_WIDTH = 40;
    const LOGO_TOP_MARGIN_FIRST_PAGE = 1;
    const HEADER_TOP_MARGIN = 1;
    doc.setFont('times', 'normal');
    doc.setFontSize(12);
    const addHeaderFooter = (isFirstPage = false) => {
      doc.setFont('times', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);

      if (!isFirstPage) {
        doc.setLineWidth(0.1);
        doc.setDrawColor(0, 102, 204);
        doc.line(
          MARGIN_LEFT,
          HEADER_TOP_MARGIN + 5,
          PAGE_WIDTH - MARGIN_RIGHT,
          HEADER_TOP_MARGIN + 5
        );
        y = HEADER_TOP_MARGIN + 10;
      }
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Página ${doc.internal.getCurrentPageInfo().pageNumber}`,
        PAGE_WIDTH / 2,
        292,
        {
          align: 'center',
        }
      );
    };
    const addSection = (title, fields) => {
      if (y + LINE_HEIGHT + 20 > PAGE_HEIGHT - 20) {
        doc.addPage();
        y = HEADER_TOP_MARGIN + 10;
        addHeaderFooter();
      }
      doc.setFont('times', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0, 102, 204);
      doc.text(title, MARGIN_LEFT, y);
      y += LINE_HEIGHT + 2;
      if (typeof doc.autoTable === 'function') {
        const tableRows = fields.map((field) => [
          field.label,
          truncateText(field.value, 60),
        ]);
        doc.autoTable({
          startY: y,
          head: [['Campo', 'Valor']],
          body: tableRows,
          theme: 'striped',
          styles: {
            font: 'times',
            fontSize: 12,
            cellPadding: TABLE_PADDING,
            textColor: [50, 50, 50],
            lineWidth: 0.1,
          },
          headStyles: {
            fillColor: [0, 102, 204],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 13,
          },
          columnStyles: {
            0: { cellWidth: 45 },
            1: { cellWidth: PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT - 45 },
          },
          margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT },
        });
        y = doc.lastAutoTable.finalY + 8;
      } else {
        console.warn(
          'jspdf-autotable não está disponível. Usando tabela manual.'
        );
        const colWidth = (PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT) / 2;
        fields.forEach((field) => {
          if (y > PAGE_HEIGHT - 15) {
            doc.addPage();
            y = HEADER_TOP_MARGIN + 10;
            addHeaderFooter();
          }
          const x = MARGIN_LEFT;
          doc.setDrawColor(200, 200, 200);
          doc.setFillColor(245, 245, 245);
          doc.rect(x, y, colWidth * 2, LINE_HEIGHT, 'FD');
          doc.setFont('times', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text(`${field.label}:`, x + TABLE_PADDING, y + 4);
          doc.setFont('times', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(50, 50, 50);
          const valueText = doc.splitTextToSize(
            truncateText(field.value, 60),
            colWidth - TABLE_PADDING * 2
          );
          doc.text(valueText, x + colWidth + TABLE_PADDING, y + 4);

          y += LINE_HEIGHT + 1;
        });
        y += 8;
      }
      doc.setLineWidth(0.1);
      doc.setDrawColor(0, 102, 204);
      doc.line(MARGIN_LEFT, y, PAGE_WIDTH - MARGIN_RIGHT, y);
      y += 8;
    };
    y = LOGO_TOP_MARGIN_FIRST_PAGE;
    try {
      if (logoBase64) {
        doc.addImage(
          logoBase64,
          'PNG',
          (PAGE_WIDTH - LOGO_MAX_WIDTH) / 2,
          y,
          LOGO_MAX_WIDTH,
          LOGO_MAX_HEIGHT,
          'logo_principal'
        );
        y += LOGO_MAX_HEIGHT + 5;
      } else {
        doc.setFont('times', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(0, 102, 204);
        doc.text('Minha Empresa Solar', PAGE_WIDTH / 2, y + 10, {
          align: 'center',
        });
        y += 20;
      }
    } catch (e) {
      console.error('Erro ao adicionar logo principal:', e);
      doc.setFont('times', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(0, 102, 204);
      doc.text('Minha Empresa Solar', PAGE_WIDTH / 2, y + 10, {
        align: 'center',
      });
      y += 20;
    }
    doc.setFont('times', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(0, 102, 204);
    doc.text('Proposta de Financiamento', PAGE_WIDTH / 2, y, {
      align: 'center',
    });
    y += 7;
    doc.setFontSize(14);
    doc.setTextColor(50, 50, 50);
    doc.text(`ID: ${data.id}`, PAGE_WIDTH / 2, y, { align: 'center' });
    y += 7;
    doc.setFontSize(12);
    doc.text(
      `Data: ${new Date().toLocaleDateString('pt-BR')}`,
      PAGE_WIDTH / 2,
      y,
      { align: 'center' }
    );
    y += 7;
    doc.setLineWidth(0.1);
    doc.setDrawColor(0, 102, 204);
    doc.line(MARGIN_LEFT, y, PAGE_WIDTH - MARGIN_RIGHT, y);
    y += 8;
    addHeaderFooter(true);
    addSection('Informações do Projeto', [
      {
        label: 'Distribuidor',
        value:
          data.projeto.distribuidor != null ? data.projeto.distribuidor : 'N/A',
      },
      {
        label: 'CNPJ Integrador',
        value: formatDocuments(data.projeto.cnpj_integrador),
      },
      {
        label: 'Telefone Integrador',
        value: data.projeto.telefone_integrador || 'N/A',
      },
      { label: 'Nome do Vendedor', value: data.projeto.nome_vendedor || 'N/A' },
      {
        label: 'Telefone do Vendedor',
        value: data.projeto.telefone_vendedor || 'N/A',
      },
      {
        label: 'Valor dos Produtos',
        value: parseCurrency(data.projeto.custo_produto),
      },
      {
        label: 'Valor do Serviço',
        value: parseCurrency(data.projeto.custo_servico),
      },
      {
        label: 'Valor Outros Produtos',
        value: parseCurrency(data.projeto.valor_outros_produtos),
      },
      {
        label: 'Valor Total Projeto',
        value: parseCurrency(data.projeto.custo_total_projeto),
      },
      {
        label: 'Quantidade de Módulos',
        value:
          data.projeto.quantidade_placas != null
            ? data.projeto.quantidade_placas
            : 'N/A',
      },
      {
        label: 'Marca dos Módulos',
        value:
          data.projeto.marca_placas != null ? data.projeto.marca_placas : 'N/A',
      },
      {
        label: 'Potência W dos Módulos',
        value:
          data.projeto.potencia_W_placas != null
            ? data.projeto.potencia_W_placas
            : 'N/A',
      },
      {
        label: 'Potência kWp Total',
        value:
          data.projeto.potencia_kWp != null
            ? data.projeto.potencia_kWp
            : data.projeto.quantidade_placas && data.projeto.potencia_W_placas
              ? (
                  (data.projeto.quantidade_placas *
                    data.projeto.potencia_W_placas) /
                  1000
                ).toFixed(2)
              : 'N/A',
      },
      {
        label: 'Quantidade de Inversores',
        value:
          data.projeto.quantidade_inversores != null
            ? data.projeto.quantidade_inversores
            : 'N/A',
      },
      {
        label: 'Marca dos Inversores',
        value:
          data.projeto.marca_inversores != null
            ? data.projeto.marca_inversores
            : 'N/A',
      },
      {
        label: 'Potência kW Total',
        value:
          data.projeto.potencia_kW_inversor != null
            ? data.projeto.potencia_kW_inversor
            : 'N/A',
      },
      {
        label: 'Geração Estimada (kWh/ano)',
        value:
          data.projeto.geracao_energia_estimada != null
            ? data.projeto.geracao_energia_estimada
            : 'N/A',
      },
      { label: 'Concessionária', value: data.projeto.concessionaria || 'N/A' },
      {
        label: 'Unidade Consumidora',
        value: data.projeto.unidade_consumidora || 'N/A',
      },
      {
        label: 'Proposta Comercial',
        value: data.projeto.cod_proposta_externa || 'N/A',
      },
    ]);
    if (data.propostas_banco.length > 0) {
      const propostasFields = data.propostas_banco
        .map((proposta, index) => [
          {
            label: `Proposta ${index + 1} - Banco`,
            value: proposta.banco || 'N/A',
          },
          {
            label: `Proposta ${index + 1} - Status`,
            value: proposta.status || 'N/A',
          },
          {
            label: `Proposta ${index + 1} - E-mail`,
            value: proposta.emailB || 'N/A',
          },
        ])
        .flat();
      addSection('Propostas Banco', propostasFields);
    }
    addSection(
      `Informações do Cliente (Pessoa ${data.cliente.tcc2 === 'juridica' ? 'Jurídica' : 'Física'})`,
      [
        {
          label: 'Tipo de Pessoa',
          value: getOptionTitle(data.cliente.tcc2, pessoFisJudi),
        },
        {
          label: 'Nome Completo',
          value: truncateText(data.cliente.nome_completo, 60) || 'N/A',
        },
        {
          label: 'CPF/CNPJ',
          value: formatDocuments(data.cliente.cpf || data.cliente.cnpj),
        },
        {
          label: 'Data de Nascimento',
          value: formatDate(data.cliente.data_nascimento),
        },
        {
          label: 'E-mail',
          value: truncateText(data.cliente.email, 60) || 'N/A',
        },
        {
          label: 'Telefone Celular',
          value: data.cliente.telefone_celular || 'N/A',
        },
        { label: 'Telefone Fixo', value: data.cliente.telefone_fixo || 'N/A' },
        {
          label: 'Estado Civil',
          value: getOptionTitle(data.cliente.estado_civil, esadoCivilOptions),
        },
        {
          label: 'Nome da Mãe',
          value: truncateText(data.cliente.nome_mae, 60) || 'N/A',
        },
        { label: 'RG', value: data.cliente.rg || 'N/A' },
        {
          label: 'Sexo',
          value: getOptionTitle(data.cliente.genero, genderOptions),
        },
        {
          label: 'Nacionalidade',
          value: getOptionTitle(
            data.cliente.nacionalidade,
            nacionalidadeOptions
          ),
        },
        { label: 'CEP', value: data.cliente.cep_cliente || 'N/A' },
        {
          label: 'Logradouro',
          value: truncateText(data.cliente.logradouro_cliente, 60) || 'N/A',
        },
        { label: 'Número', value: data.cliente.numero_cliente || 'N/A' },
        {
          label: 'Complemento',
          value: truncateText(data.cliente.complemento, 60) || 'N/A',
        },
        {
          label: 'Bairro',
          value: truncateText(data.cliente.bairro, 60) || 'N/A',
        },
        {
          label: 'Cidade/Estado',
          value: `${truncateText(data.cliente.cidade, 30) || 'N/A'} / ${data.cliente.uf || 'N/A'}`,
        },
      ]
    );
    if (data.cliente.tcc2 !== 'juridica') {
      addSection('Informações Profissionais', [
        {
          label: 'Profissão',
          value: truncateText(data.profissional.profissao, 60) || 'N/A',
        },
        {
          label: 'Ocupação',
          value: truncateText(data.profissional.ocupacao, 60) || 'N/A',
        },
        {
          label: 'Tempo de Empresa (Meses)',
          value: data.profissional.tempo_empresa || 'N/A',
        },
        {
          label: 'Nome da Empresa',
          value: truncateText(data.profissional.nome_empresa, 60) || 'N/A',
        },
        {
          label: 'Telefone da Empresa',
          value: data.profissional.telefone_empresa || 'N/A',
        },
        {
          label: 'Renda Mensal',
          value: parseCurrency(data.profissional.renda_mensal),
        },
        {
          label: 'Patrimônio Estimado',
          value: parseCurrency(data.profissional.patrimonio_estimado),
        },
        {
          label: 'Situação do Imóvel',
          value:
            data.profissional.situacao_imovel != null
              ? getOptionTitle(data.profissional.situacao_imovel, situacOptions)
              : 'N/A',
        },
      ]);
    }
    addSection('Endereço de Instalação', [
      { label: 'CEP', value: data.instalacao.cep || 'N/A' },
      {
        label: 'Logradouro',
        value: truncateText(data.instalacao.rua, 60) || 'N/A',
      },
      { label: 'Número', value: data.instalacao.numero || 'N/A' },
      {
        label: 'Complemento',
        value: truncateText(data.instalacao.complemento, 60) || 'N/A',
      },
      {
        label: 'Bairro',
        value: truncateText(data.instalacao.bairro, 60) || 'N/A',
      },
      {
        label: 'Cidade/Estado',
        value: `${truncateText(data.instalacao.cidade, 30) || 'N/A'} / ${data.instalacao.estado || 'N/A'}`,
      },
    ]);
    doc.save(`Proposta_Espelho_${id}.pdf`);
  }, [
    id,
    register,
    registerPartner,
    registerPersonal,
    registerInstall,
    registerProposals,
    isPf,
    logoBase64,
  ]);
  const formItemsProjeto = [
    {
      ref: 'pre_proposal',
      placeholder: 'Pré-Proposta',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'distribuidor',
      placeholder: 'Distribuidor',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'cnpj_integrador',
      placeholder: 'CNPJ Integrador',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'telefone_integrador',
      placeholder: 'Telefone Integrador',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'nome_vendedor',
      placeholder: 'Nome Completo do Vendedor',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'telefone_vendedor',
      placeholder: 'Telefone do Vendedor',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'custo_produto',
      type: 'money',
      placeholder: 'Valor dos Produtos',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'custo_servico',
      type: 'money',
      placeholder: 'Valor do Serviço',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_outros_produtos',
      type: 'money',
      placeholder: 'Valor Outros',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'custo_total_projeto',
      type: 'money',
      placeholder: 'Valor Total',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'quantidade_placas',
      type: 'number',
      placeholder: 'Quantidade de Módulos',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'marca_placas',
      placeholder: 'Marca dos Módulos',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'potencia_W_placas',
      type: 'number',
      placeholder: 'Potência W dos Módulos',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'potencia_kWp',
      type: 'number',
      placeholder: 'Potência kWp Total',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'quantidade_inversores',
      type: 'number',
      placeholder: 'Quantidade de Inversores',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'marca_inversores',
      placeholder: 'Marca dos Inversores',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'potencia_kW_inversor',
      type: 'number',
      placeholder: 'Potência kW Total',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'geracao_energia_estimada',
      type: 'number',
      placeholder: 'Geração de Energia kW/h no Primeiro Ano',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'concessionaria',
      placeholder: 'Concessionária de Energia',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'unidade_consumidora',
      placeholder: 'Unidade Consumidora',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'cod_proposta_externa',
      placeholder: 'Número da Proposta Comercial',
      disabled: true,
      transparent: false,
    },
  ];
  const formCliente = [
    {
      ref: 'tcc2',
      placeholder: 'Tipo de Pessoa',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'nome_completo',
      placeholder: 'Nome Completo',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'cpf',
      placeholder: registerPartner?.tcc2 === 'juridica' ? 'CNPJ' : 'CPF',
      type: 'text',
      disabled: true,
      value: registerPartner?.cpf,
      label: registerPartner?.tcc2 === 'juridica' ? 'CNPJ' : 'CPF',
      transparent: false,
    },
    {
      ref: 'data_nascimento',
      type: 'date',
      placeholder: 'Data de Nascimento',
      disabled: true,
      transparent: false,
    },
    { ref: 'email', placeholder: 'E-mail', disabled: true, transparent: false },
    {
      ref: 'telefone_celular',
      placeholder: 'Telefone Celular',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'telefone_fixo',
      placeholder: 'Telefone Fixo',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'estado_civil',
      placeholder: 'Estado Civil',
      disabled: true,
      display: isPf ? true : false,
      transparent: false,
    },
    {
      ref: 'nome_mae',
      placeholder: 'Nome da Mãe',
      disabled: true,
      display: isPf ? true : false,
      transparent: false,
    },
    {
      ref: 'rg',
      placeholder: 'RG',
      disabled: true,
      display: isPf ? true : false,
      transparent: false,
    },
    {
      ref: 'genero',
      placeholder: 'Sexo',
      disabled: true,
      display: isPf ? true : false,
      transparent: false,
    },
    {
      ref: 'nacionalidade',
      placeholder: 'Nacionalidade',
      disabled: true,
      display: isPf ? true : false,
      transparent: false,
    },
    {
      ref: 'cep_cliente',
      placeholder: 'CEP',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'logradouro_cliente',
      placeholder: 'Logradouro',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'numero_cliente',
      placeholder: 'Número',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'complemento',
      placeholder: 'Complemento',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'bairro',
      placeholder: 'Bairro',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'cidade',
      placeholder: 'Cidade',
      disabled: true,
      transparent: false,
    },
    { ref: 'uf', placeholder: 'Estado', disabled: true, transparent: false },
  ];
  const formProfessional = [
    {
      ref: 'profissao',
      placeholder: 'Profissão',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'ocupacao',
      placeholder: 'Ocupação',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'tempo_empresa',
      placeholder: 'Tempo de Empresa (Meses)',
      type: 'number',
      disabled: true,
      min: 0,
      transparent: false,
    },
    {
      ref: 'nome_empresa',
      placeholder: 'Nome da Empresa',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'telefone_empresa',
      placeholder: 'Telefone da Empresa',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'renda_mensal',
      placeholder: 'Renda Mensal',
      type: 'money',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'patrimonio_estimado',
      placeholder: 'Patrimônio Estimado',
      type: 'money',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'situacao_imovel',
      placeholder: 'Situação do Imóvel',
      disabled: true,
      transparent: false,
    },
  ];
  const formItemsAddress = [
    {
      ref: 'cep',
      placeholder: 'CEP',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'rua',
      placeholder: 'Logradouro',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'numero',
      placeholder: 'Número',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'complemento',
      placeholder: 'Complemento',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'bairro',
      placeholder: 'Bairro',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'cidade',
      placeholder: 'Cidade',
      disabled: true,
      transparent: false,
    },
    { ref: 'uf', placeholder: 'Estado', disabled: true, transparent: false },
  ];
  const formItemsProposals = [
    { ref: 'banco', placeholder: 'Banco', disabled: true, transparent: false },
    {
      ref: 'status',
      placeholder: 'Status',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'emailB',
      placeholder: 'E-mail do Banco',
      disabled: true,
      transparent: false,
    },
  ];
  const init = useCallback(async () => {
    if (!id) {
      setError('ID da proposta não fornecido.');
      return;
    }
    try {
      const result = await ReadOne(id);
      if (result && !exposeStrapiError(result)) {
        const projeto = result?.informacoes_projeto || {};
        const profissional = result?.informacoes_profissionais || {};
        const cliente = result?.informacoes_cliente || {};
        projeto.cnpj_integrador = formatDocuments(result.integrador?.cnpj);
        projeto.telefone_integrador = result?.telefone_integrador || 'N/A';
        projeto.telefone_vendedor =
          result?.informacoes_projeto?.telefone_vendedor || 'N/A';
        let distribuidor =
          result?.distribuidor ||
          projeto.distribuidor ||
          projeto.distributor ||
          projeto.distribuidora ||
          null;
        if (!user?.administrador && distribuidor === null) {
          distribuidor = user?.distribuidor?.id || null;
        }
        const situacaoImovelValue =
          profissional.situacao_imovel ||
          profissional.property_status ||
          result?.situacao_imovel ||
          null;
        let distribuidorNome =
          typeof distribuidor === 'object' && distribuidor !== null
            ? distribuidor.trade_name
            : distribuidor;
        setRegister({
          ...projeto,
          ...result?.integrador,
          pre_proposal: result?.pre_proposal?.id || 'N/A',
          distribuidor: distribuidorNome,
          quantidade_placas:
            projeto.quantidade_placas || projeto.module_quantity || null,
          marca_placas: projeto.marca_placas || projeto.module_brand || null,
          potencia_W_placas:
            projeto.potencia_W_placas || projeto.module_power || null,
          potencia_kWp:
            projeto.potencia_kWp ||
            projeto.total_kWp ||
            (projeto.quantidade_placas && projeto.potencia_W_placas
              ? (
                  (projeto.quantidade_placas * projeto.potencia_W_placas) /
                  1000
                ).toFixed(2)
              : null),
          quantidade_inversores:
            projeto.quantidade_inversores || projeto.inverter_quantity || null,
          marca_inversores:
            projeto.marca_inversores || projeto.inverter_brand || null,
          potencia_kW_inversor:
            projeto.potencia_kW_inversor || projeto.inverter_power || null,
          geracao_energia_estimada:
            projeto.geracao_energia_estimada ||
            projeto.estimated_generation ||
            null,
          telefone_integrador: formatPhone('', projeto.telefone_integrador),
          telefone_vendedor: formatPhone('', projeto.telefone_vendedor),
          valor_outros_produtos:
            projeto.custo_total_projeto -
              projeto.custo_produto -
              projeto.custo_servico || 0,
          cod_proposta_externa:
            normalizeName(projeto.cod_proposta_externa) || 'N/A',
        });
        setRegisterInstall(
          {
            ...result?.endereco_instalacao,
            cep: formatCEP(result?.endereco_instalacao?.cep),
          } || {}
        );
        setRegisterPersonal({
          ...profissional,
          situacao_imovel: situacaoImovelValue,
          profissao: normalizeName(profissional.profissao),
          ocupacao: normalizeName(profissional.ocupacao),
          telefone_empresa: formatPhone('', profissional.telefone_empresa),
        });
        setRegisterPartner({
          ...cliente,
          ...result?.endereco_residencial,
          tcc2: normalizeName(cliente?.tipo_cliente),
          telefone_celular: formatPhone('', cliente?.telefone_celular),
          telefone_fixo: formatPhone('', cliente?.telefone_fixo),
          cpf:
            cliente?.tipo_cliente === 'juridica' ? cliente?.cnpj : cliente?.cpf,
          cnpj: cliente?.tipo_cliente === 'juridica' ? cliente?.cnpj : null,
          rg: cliente?.rg || 'Não informado',
          estado_civil: normalizeName(cliente?.estado_civil),
          genero: normalizeName(cliente?.genero),
          cep_cliente: formatCEP(result?.endereco_residencial?.cep),
          logradouro_cliente: result?.endereco_residencial?.rua,
          numero_cliente: result?.endereco_residencial?.numero,
          complemento_cliente: result?.endereco_residencial?.complemento,
          bairro_cliente: result?.endereco_residencial?.bairro,
          cidade_cliente: result?.endereco_residencial?.cidade,
          estado_cliente: result?.endereco_residencial?.uf,
        });
        const allProposals = result?.propostas_banco || [];
        const mappedProposals = allProposals.map((p) => ({
          banco: p.banco?.nome_fantasia || p.banco || 'N/A',
          status: p.status
            ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase()
            : 'N/A',
          emailB: p.banco?.email || 'N/A',
        }));
        setRegisterProposals(mappedProposals);
        setError(null);
      } else {
        setError('Erro ao carregar os dados da proposta.');
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setError('Ocorreu um erro ao carregar os dados. Tente novamente.');
    }
  }, [id, user]);

  useEffect(() => init(), [init]);

  if (error) {
    return (
      <ContainerAuthenticated>
        <Title>Espelho da Proposta {id}</Title>
        <p>{error}</p>
        <Button
          color="blue"
          width={'fit-content'}
          small
          nospace
          onClick={() => history.goBack()}
        >
          Voltar
        </Button>
      </ContainerAuthenticated>
    );
  }
  if (!register || !registerInstall || !registerPartner) {
    return (
      <ContainerAuthenticated>
        <Title>Espelho da Proposta {id}</Title>
        <p>Carregando dados da proposta...</p>
      </ContainerAuthenticated>
    );
  }
  if (isPf && !registerPersonal) {
    return (
      <ContainerAuthenticated>
        <Title>Espelho da Proposta {id}</Title>
        <p>Carregando dados da proposta...</p>
      </ContainerAuthenticated>
    );
  }

  return (
    <ContainerAuthenticated>
      <Title>Espelho da Proposta {id}</Title>
      <FinancingProposalFormsWrapper>
        <FinancingProposalHeader>
          <Title small nomargin>
            Informações do Projeto
          </Title>
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <Button
              color="blue"
              width={'fit-content'}
              small
              nospace
              onClick={() => history.goBack()}
            >
              Voltar
            </Button>
            <Button
              color="green"
              width={'fit-content'}
              small
              nospace
              onClick={generateFullProposalMirror}
            >
              Gerar Espelho PDF
            </Button>
          </div>
        </FinancingProposalHeader>
        <FormCore
          ref={refForm}
          register={register}
          formItems={formItemsProjeto}
          readOnly
        />
        <FormSpacing />
        {registerProposals.length > 0 && (
          <>
            <FinancingProposalHeader>
              <Title small nomargin>
                Propostas Banco
              </Title>
            </FinancingProposalHeader>
            {registerProposals.map((proposal, index) => (
              <Fragment key={index}>
                <Title small nomargin style={{ marginTop: '10px' }}>
                  Proposta {index + 1}
                </Title>
                <FormCore
                  register={{
                    ...proposal,
                    banco: proposal.banco || 'N/A',
                    status: proposal.status || 'N/A',
                    emailB: proposal.emailB || 'N/A',
                  }}
                  formItems={formItemsProposals}
                  readOnly
                />
                {index < registerProposals.length - 1 && (
                  <FormSpacing style={{ height: '10px' }} />
                )}
              </Fragment>
            ))}
            <FormSpacing />
          </>
        )}
        <FinancingProposalHeader>
          <Title small nomargin>
            Informações do Cliente (Pessoa {isPf ? 'Física' : 'Jurídica'})
          </Title>
        </FinancingProposalHeader>
        <FormCore
          ref={refFormPJPF}
          register={registerPartner}
          formItems={formCliente.filter((item) => item.display !== false)}
          readOnly
        />
        <FormSpacing />

        {isPf && registerPersonal && (
          <>
            <FinancingProposalHeader>
              <Title small nomargin>
                Informações Profissionais
              </Title>
            </FinancingProposalHeader>
            <FormCore
              register={registerPersonal}
              formItems={formProfessional}
              readOnly
            />
            <FormSpacing />
          </>
        )}

        <FinancingProposalHeader>
          <Title small nomargin>
            Endereço de Instalação
          </Title>
        </FinancingProposalHeader>
        <FormCore
          ref={refFormAddressInstall}
          register={registerInstall}
          formItems={formItemsAddress}
          readOnly
        />
        <FormSpacing />
      </FinancingProposalFormsWrapper>
    </ContainerAuthenticated>
  );
}
